import React from 'react';
import styles from './Table.module.css';

// 테이블 컬럼 타입 정의
export interface Column<T> {
    key: keyof T | string;                  // 데이터 객체의 키 값
    header: string | React.ReactNode;       // 컬럼 헤더 내용
    width?: string;                         // 컬럼 너비 (선택적)
    sortable?: boolean;                     // 정렬 가능 여부 (선택적)
    render?: (value: any, row: T, index: number) => React.ReactNode;  // 커스텀 렌더링 함수 (선택적)
}

// 정렬 상태 인터페이스
interface SortState {
    field: string | null;                   // 정렬 기준 필드
    direction: 'asc' | 'desc' | null;       // 정렬 방향
}

// TableSection 컴포넌트 props 타입 정의
interface TableSectionProps<T> {
    columns: Column<T>[];                   // 컬럼 정의 배열
    data: T[];                             // 표시할 데이터 배열
    loading: boolean;                       // 로딩 상태
    sort: SortState;                        // 정렬 상태
    onSort: (field: string | null, direction: 'asc' | 'desc' | null) => void;  // 정렬 변경 핸들러
    onRowClick?: (row: T) => void;         // 행 클릭 핸들러 (선택적)
}

// 정렬 아이콘 컴포넌트 props 타입 정의
interface SortIconProps {
    direction: 'asc' | 'desc' | null;       // 정렬 방향
    active: boolean;                        // 활성화 상태
}

// 정렬 아이콘 컴포넌트
const SortIcon: React.FC<SortIconProps> = ({direction, active}) => {
    // 아이콘 클래스 생성 함수
    const getIconClasses = () => {
        const baseClasses = [styles.sortIcon];
        if (!active) baseClasses.push(styles.sortIconInactive);
        if (active && direction === 'desc') baseClasses.push(styles.sortIconDesc);
        return baseClasses.join(' ');
    };

    return (
        <svg
            className={getIconClasses()}
            viewBox="0 0 10 6"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M5 6L0 0h10L5 6z"/>
        </svg>
    );
};

// 테이블 섹션 컴포넌트
function TableSection<T extends Record<string, any>>({
                                                         columns,
                                                         data,
                                                         loading,
                                                         sort,
                                                         onSort,
                                                         onRowClick
                                                     }: TableSectionProps<T>) {
    // 정렬 처리 함수
    const handleSort = (column: Column<T>) => {
        if (!column.sortable) return;

        const isSameColumn = sort.field === column.key;
        const currentDirection = sort.direction;

        // 정렬 방향 결정 (asc -> desc -> null -> asc)
        let newDirection: 'asc' | 'desc' | null = 'asc';

        if (isSameColumn) {
            if (currentDirection === 'asc') {
                newDirection = 'desc';
            } else if (currentDirection === 'desc') {
                newDirection = null;
            }
        }

        onSort(
            newDirection === null ? null : column.key as string,
            newDirection
        );
    };

    // 셀 값 포맷팅 함수
    const formatValue = (value: any): string => {
        if (value === null || value === undefined) {
            return '-';
        }

        if (typeof value === 'object' && 'toLocaleDateString' in value) {
            return value.toLocaleDateString();  // 날짜 객체 처리
        }

        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';        // 불리언 값 처리
        }

        return String(value);                   // 기타 값들은 문자열로 변환
    };

    // 셀 렌더링 함수
    const renderCell = (column: Column<T>, item: T, index: number) => {
        const value = item[column.key as keyof T];
        // 커스텀 렌더링 함수가 있으면 사용, 없으면 기본 포맷팅
        if (column.render) {
            return column.render(value, item, index);
        }
        return formatValue(value);
    };

    return (
        <div className={styles.tableWrapper}>
            <table className={styles.table}>
                {/* 테이블 헤더 */}
                <thead>
                <tr className={styles.tableHeader}>
                    {columns.map((column) => (
                        <th
                            key={String(column.key)}
                            className={`${styles.headerCell} ${column.sortable ? styles.sortable : ''}`}
                            style={{width: column.width}}
                        >
                            <div
                                className={`
                                    ${styles.headerContent}
                                    ${column.sortable ? styles.sortableHeader : ''}
                                    ${sort.field === column.key ? styles.activeSortHeader : ''}
                                `}
                                onClick={() => handleSort(column)}
                                role={column.sortable ? 'button' : undefined}
                                tabIndex={column.sortable ? 0 : undefined}
                            >
                                <span>{column.header}</span>
                                {column.sortable && (
                                    <SortIcon
                                        direction={sort.field === column.key ? sort.direction : null}
                                        active={sort.field === column.key}
                                    />
                                )}
                            </div>
                        </th>
                    ))}
                </tr>
                </thead>
                {/* 테이블 본문 */}
                <tbody className={styles.tableBody}>
                {loading ? (
                    // 로딩 상태 표시
                    <tr>
                        <td colSpan={columns.length} className={styles.loadingCell}>
                            Loading...
                        </td>
                    </tr>
                ) : data.length === 0 ? (
                    // 데이터가 없을 때 메시지 표시
                    <tr>
                        <td colSpan={columns.length} className={styles.emptyCell}>
                            No data available
                        </td>
                    </tr>
                ) : (
                    // 데이터 행 렌더링
                    data.map((item, index) => (
                        <tr
                            key={index}
                            className={`${styles.tableRow} ${onRowClick ? styles.clickable : ''}`}
                            onClick={() => onRowClick?.(item)}
                            role={onRowClick ? "button" : "row"}
                            tabIndex={onRowClick ? 0 : undefined}
                        >
                            {columns.map((column) => (
                                <td key={String(column.key)} className={styles.tableCell}>
                                    {renderCell(column, item, index)}
                                </td>
                            ))}
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        </div>
    );
}

export default TableSection;